import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { useAuth } from '../../auth/AuthContext';
import { db } from '../../firebase-client';
import { collection, doc, getDoc } from 'firebase/firestore';
import { safeOnSnapshot } from '../../utils/firestoreSubscriptions';
import { Link, useNavigate } from 'react-router-dom';
import { buildMirrorCalendarEvents } from '../../utils/calendarMirrorEvents';
import { useOnlineRefreshSignal } from '../../utils/onlineStatus';
import { readOfflineContentCache, writeOfflineContentCache } from '../../utils/offlineContentCache';
import { CalendarButton } from '../ui/CalendarButton';
import './upcomingEvents.css';

const CACHE_SCOPE = 'calendar';
function formatListEvent(event) {
  const data = event.details || {};
  const start = event.start instanceof Date ? event.start : new Date(event.start);
  const baseEventId = data.originalEventId || String(data.eventId || '').split('__')[0];
  return {
    ...data,
    name: event.title || data.name || 'Event',
    start,
    formattedTime: `${start.toLocaleDateString('en-GB')} @ ${start.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    })}`,
    mode: data.mode || data.eventMode || 'inPerson',
    onlineUrl: data.onlineUrl || '',
    location: data.location || {},
    ticketWalletPath: data.attendanceMode === 'ticketed'
      && data.ticketStatus === 'paid'
      && data.teamId
      && baseEventId
      ? `/tickets/${encodeURIComponent(data.teamId)}/${encodeURIComponent(baseEventId)}`
      : ''
  };
}
export default function UpcomingEventsCard() {
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const [userEvents, setUserEvents] = useState([]);
  const [familyEvents, setFamilyEvents] = useState([]);
  const [familyNames, setFamilyNames] = useState({});
  const [hasFamilies, setHasFamilies] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadedEventsUserId, setLoadedEventsUserId] = useState('');
  const onlineRefreshSignal = useOnlineRefreshSignal();
  useEffect(() => {
    if (!user?.uid) {
      setUserEvents([]);
      setFamilyEvents([]);
      setFamilyNames({});
      setHasFamilies(false);
      setLoadedEventsUserId('');
      setLoading(false);
      return undefined;
    }
    let unsubUser = null;
    const familyUnsubs = [];
    const famEventsMap = new Map(); // famId -> array of events
    const userEventCacheParts = [user.uid, CACHE_SCOPE, 'upcomingUserEvents'];
    const familyEventCacheParts = [user.uid, CACHE_SCOPE, 'upcomingFamilyEvents'];
    const familyNameCacheParts = [user.uid, CACHE_SCOPE, 'familyNames'];
    const cachedUserEvents = readOfflineContentCache(userEventCacheParts, []);
    const cachedFamilyEvents = readOfflineContentCache(familyEventCacheParts, []);
    const cachedFamilyNames = readOfflineContentCache(familyNameCacheParts, {});
    if (cachedUserEvents.length > 0 || cachedFamilyEvents.length > 0) {
      setUserEvents(cachedUserEvents);
      setFamilyEvents(cachedFamilyEvents);
      setFamilyNames(cachedFamilyNames);
      setHasFamilies(Object.keys(cachedFamilyNames).length > 0);
      setLoading(false);
    }

    const hydrateFamilies = async () => {
      if (cachedUserEvents.length === 0 && cachedFamilyEvents.length === 0) setLoading(true);
      try {
        const userDocSnap = await getDoc(doc(db, 'users', user.uid));
        const familyRoles = userDocSnap.data()?.family_roles || {};
        const familyIds = Object.keys(familyRoles);
        if (familyIds.length === 0 && userDocSnap.metadata?.fromCache && Object.keys(cachedFamilyNames).length > 0) {
          return;
        }
        setHasFamilies(familyIds.length > 0);

        // Resolve family names
        const names = {};
        for (const famId of familyIds) {
          const famDoc = await getDoc(doc(db, `families/${famId}`));
          names[famId] = famDoc.exists() ? famDoc.data().name : 'Unnamed Family';
        }
        setFamilyNames(names);
        if (Object.keys(names).length > 0 || !userDocSnap.metadata?.fromCache) {
          writeOfflineContentCache(familyNameCacheParts, names);
        }

        // Subscribe to family calendars
        familyIds.forEach(famId => {
          const ref = collection(db, `families/${famId}/calendar`);
          const unsub = safeOnSnapshot(ref, snap => {
            const now = new Date();
            const rows = snap.docs.map(d => ({
              id: d.id,
              data: d.data() || {}
            }));
            const evs = buildMirrorCalendarEvents(rows, {
              now,
              maxOccurrences: 1,
              seriesMode: 'next'
            }).map(formatListEvent).filter(e => e && e.start > now).map(e => ({
              ...e,
              familyId: famId
            }));
            famEventsMap.set(famId, evs);

            // Flatten + sort all family events
            const merged = Array.from(famEventsMap.values()).flat();
            merged.sort((a, b) => a.start - b.start);
            const nextFamilyEvents = merged.slice(0, 5);
            if (nextFamilyEvents.length === 0 && snap.metadata?.fromCache && cachedFamilyEvents.length > 0) return;
            setFamilyEvents(nextFamilyEvents);
            if (nextFamilyEvents.length > 0 || !snap.metadata?.fromCache) {
              writeOfflineContentCache(familyEventCacheParts, nextFamilyEvents);
            }
          }, () => {});
          familyUnsubs.push(unsub);
        });
      } catch {
        // Keep the card usable with cached or private calendar data if family hydration fails.
      } finally {
        setLoadedEventsUserId(user.uid);
        setLoading(false);
      }
    };

    // Subscribe to user calendar
    unsubUser = safeOnSnapshot(collection(db, `privateUsers/${user.uid}/calendar`), snap => {
      const now = new Date();
      const rows = snap.docs.map(d => ({
        id: d.id,
        data: d.data() || {}
      }));
      const upcomingUserEvents = buildMirrorCalendarEvents(rows, {
        now,
      maxOccurrences: 1,
      seriesMode: 'next'
    }).map(formatListEvent).filter(e => e && e.start > now).sort((a, b) => a.start - b.start).slice(0, 5);
      if (upcomingUserEvents.length === 0 && snap.metadata?.fromCache && cachedUserEvents.length > 0) return;
      setUserEvents(upcomingUserEvents);
      if (upcomingUserEvents.length > 0 || !snap.metadata?.fromCache) {
        writeOfflineContentCache(userEventCacheParts, upcomingUserEvents);
      }
    }, () => {});
    hydrateFamilies();
    return () => {
      if (unsubUser) unsubUser();
      familyUnsubs.forEach(u => u && u());
    };
  }, [onlineRefreshSignal, user?.uid]);
  const hasUserEvents = userEvents.length > 0;
  const hasFamilyEvents = familyEvents.length > 0;
  const hasResolvedEvents = loadedEventsUserId === user?.uid;

  return <>
      <Card title="Your Upcoming Events" className="dashboard-card">
        {loading && !hasResolvedEvents ? <p>Loading events…</p> : !hasUserEvents ? <p style={{
        color: 'var(--clr-text-muted)'
      }}>No upcoming events found.</p> : <ul style={{
        paddingLeft: '1rem',
        marginTop: '0.5rem'
      }}>
            {userEvents.map((event, i) => <li key={i} className="upcoming-event-row">
                <span>{event.name} – {event.formattedTime}
                  {event.mode === 'inPerson' && event.location?.name && <> • {event.location.name}</>}
                  {event.mode === 'online' && event.onlineUrl && <> • <a href={event.onlineUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Join Online</a></>}
                </span>
                {event.ticketWalletPath && <Link className="button button-primary button--sm upcoming-event-row__tickets" to={event.ticketWalletPath}>View tickets</Link>}
              </li>)}
          </ul>}

        <div className="form-actions" style={{
        marginTop: '1rem'
      }}>
          <CalendarButton onClick={() => navigate('/calendar/user')}>
            View Full Calendar
          </CalendarButton>
        </div>
      </Card>

      {hasFamilies && <Card title="Family Events" className="dashboard-card">
          {loading && !hasResolvedEvents ? <p>Loading events…</p> : !hasFamilyEvents ? <p style={{
        color: 'var(--clr-text-muted)'
      }}>No family events found.</p> : <ul style={{
        paddingLeft: '1rem',
        marginTop: '0.5rem'
      }}>
              {familyEvents.map((event, i) => <li key={i}>
                  {event.name} – {event.formattedTime}
                  {event.mode === 'inPerson' && event.location?.name && <> • {event.location.name}</>}
                  {event.mode === 'online' && event.onlineUrl && <> • <a href={event.onlineUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Join Online</a></>} ({familyNames[event.familyId]})
                </li>)}
            </ul>}

          {Object.keys(familyNames).map(famId => <div key={famId} className="form-actions" style={{
        marginTop: '1rem'
      }}>
              <CalendarButton onClick={() => navigate(`/calendar/family/${famId}`)}>
                View {familyNames[famId]} Calendar
              </CalendarButton>
            </div>)}
        </Card>}
    </>;
}
